/**
 * AD-TECH 顶层应用组件（CLAUDE.md §前端规范）。
 *
 * 路由：
 *   /login                              → 登录页（未登录也可访问）
 *   /tencent（重定向到 ad-name-template）→ 腾讯广告默认页
 *   /tencent/ad-name-template           → 广告命名模板编辑器
 *   /tencent/redline                    → 红线规则配置
 *   /tencent/strategy                   → 策略配置（广告操作开关）
 *   /tencent/auto-operate-result        → 策略运行记录（do_check 执行结果日志）
 *   /tencent/t0-dashboard               → T0广告看板（筛选占位 + 自定义账户集合管理）
 *   /tencent/auto-operate-dashboard      → DashBoard（操作原因聚合展示）
 *   /tencent/user-token-auth            → 授权更新（USER_TOKEN 扫码实名认证二维码）
 *   /ocean（重定向到 redline）           → 巨量广告
 *   /ocean/redline                    → 红线规则配置
 *   /ocean/{strategy,auto-operate-result} → 巨量广告其余子页（空页）
 *   /baidu（重定向到 redline）           → 百度广告
 *   /baidu/{redline,strategy,auto-operate-result} → 百度广告三子页（空页）
 *   /huawei（重定向到 ad-name-template）→ 华为应用商店默认页
 *   /huawei/ad-name-template           → 广告命名模板编辑器（华为）
 *   /huawei/redline                    → 红线规则配置（华为）
 *   /huawei/strategy                   → 策略配置（华为商店控停策略，仅指定用户可编辑）
 *   /huawei/auto-operate-result        → 策略运行记录（华为，huawei_red_check 动作日志）
 *   /apple（重定向到 ad-name-template）→ Apple Ads 默认页
 *   /apple/ad-name-template           → 广告命名模板编辑器（Apple Ads，广告系列/广告组两组）
 *   /apple/redline                    → 红线规则配置（Apple Ads，投放时间 + 豁免关键词）
 *   /apple/strategy                   → 策略配置（Apple Ads 目标费率，仅指定用户可编辑）
 *   /apple/auto-operate-result        → 策略运行记录（Apple Ads，t_apple_watch_strategy_result 盯盘/红线命中日志）
 *   /honor（重定向到 ad-name-template）→ 荣耀默认页
 *   /honor/{ad-name-template,redline,strategy,auto-operate-result} → 荣耀四子页（空页）
 *   /vivo（重定向到 ad-name-template）→ VIVO 默认页
 *   /vivo/{ad-name-template,redline,strategy,auto-operate-result} → VIVO 四子页（空页）
 *   /my-resources（重定向到 business-city）→ 我的资源默认页
 *   /my-resources/business-city      → 展业城市（跨平台共享展业地域维表只读页）
 *
 * 鉴权：除 /login 外，未登录用户被重定向至 /login；登录后回到原目标路径。
 */

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./contexts/AuthContext";
import { TopNav } from "./components/TopNav";
import { Sidebar } from "./components/Sidebar";
import type { PlatformKey } from "./components/Sidebar";
import { ToastViewport } from "./components/ToastViewport";
import LoginPage from "./pages/LoginPage";
import { EmptyPage } from "./pages/EmptyPage";
import AdNameTemplatePage from "./pages/tencent/AdNameTemplatePage";
import HuaweiAdNameTemplatePage from "./pages/huawei/AdNameTemplatePage";
import AppleAdNameTemplatePage from "./pages/apple/AdNameTemplatePage";
import AppleRedlineConfigPage from "./pages/apple/RedlineConfigPage";
import AppleStrategyConfigPage from "./pages/apple/StrategyConfigPage";
import AppleAutoAdOperateResultPage from "./pages/apple/AutoAdOperateResultPage";
import HuaweiRedlineConfigPage from "./pages/huawei/RedlineConfigPage";
import HuaweiStrategyConfigPage from "./pages/huawei/StrategyConfigPage";
import HuaweiAutoAdOperateResultPage from "./pages/huawei/AutoAdOperateResultPage";
import AutoAdOperateResultPage from "./pages/tencent/AutoAdOperateResultPage";
import AutoAdOperateDashboardPage from "./pages/tencent/AutoAdOperateDashboardPage";
import RedlineConfigPage from "./pages/tencent/RedlineConfigPage";
import StrategyConfigPage from "./pages/tencent/StrategyConfigPage";
import T0DashboardPage from "./pages/tencent/T0DashboardPage";
import UserTokenAuthPage from "./pages/tencent/UserTokenAuthPage";
import OceanRedlineConfigPage from "./pages/ocean/OceanRedlineConfigPage";
import BusinessCityPage from "./pages/myResources/BusinessCityPage";

function RequireAuth({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * 平台壳：顶栏 + 冻结侧栏 + 唯一滚动 main。
 * 外层 h-screen + overflow-hidden：约束整页高度为视口，使 main 成为唯一滚动容器，
 * 侧栏（行内 flex 兄弟，高度=行高）与顶栏随之固定，用户下拉页面时各平台侧栏位置均不变。
 * 腾讯/巨量/百度/华为/Apple Ads/荣耀/VIVO 七平台共用。
 */
function PlatformShell({
  platform,
  children,
}: {
  platform: PlatformKey;
  children: ReactNode;
}) {
  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <TopNav active={platform} />
      <div className="flex flex-1 min-h-0">
        <Sidebar platform={platform} />
        <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

/** 巨量/百度/Apple Ads/荣耀/VIVO 的空子页：鉴权 + 平台壳 + 「页面内容为空」 */
function EmptyRoute({
  platform,
  title,
}: {
  platform: PlatformKey;
  title: string;
}) {
  return (
    <RequireAuth>
      <PlatformShell platform={platform}>
        <EmptyPage title={title} />
      </PlatformShell>
    </RequireAuth>
  );
}

/** 巨量/百度落地页：重定向到首个子页（红线规则配置） */
function PlatformIndexRedirect({ platform }: { platform: PlatformKey }) {
  return (
    <RequireAuth>
      <PlatformShell platform={platform}>
        <Navigate to={`/${platform}/redline`} replace />
      </PlatformShell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Tencent routes */}
        <Route
          path="/tencent"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <Navigate to="/tencent/ad-name-template" replace />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/ad-name-template"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <AdNameTemplatePage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/redline"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <RedlineConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/auto-operate-result"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <AutoAdOperateResultPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/t0-dashboard"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <T0DashboardPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/auto-operate-dashboard"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <AutoAdOperateDashboardPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/strategy"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <StrategyConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/tencent/user-token-auth"
          element={
            <RequireAuth>
              <PlatformShell platform="tencent">
                <UserTokenAuthPage />
              </PlatformShell>
            </RequireAuth>
          }
        />

        {/* Ocean（巨量广告）routes */}
        <Route
          path="/ocean"
          element={<PlatformIndexRedirect platform="ocean" />}
        />
        <Route
          path="/ocean/redline"
          element={
            <RequireAuth>
              <PlatformShell platform="ocean">
                <OceanRedlineConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/ocean/strategy"
          element={<EmptyRoute platform="ocean" title="策略配置" />}
        />
        <Route
          path="/ocean/auto-operate-result"
          element={<EmptyRoute platform="ocean" title="策略运行记录" />}
        />

        {/* Baidu（百度广告）routes — 三子页均为空页 */}
        <Route
          path="/baidu"
          element={<PlatformIndexRedirect platform="baidu" />}
        />
        <Route
          path="/baidu/redline"
          element={<EmptyRoute platform="baidu" title="红线规则配置" />}
        />
        <Route
          path="/baidu/strategy"
          element={<EmptyRoute platform="baidu" title="策略配置" />}
        />
        <Route
          path="/baidu/auto-operate-result"
          element={<EmptyRoute platform="baidu" title="策略运行记录" />}
        />

        {/* Huawei（华为应用商店）routes */}
        <Route
          path="/huawei"
          element={
            <RequireAuth>
              <PlatformShell platform="huawei">
                <Navigate to="/huawei/ad-name-template" replace />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/huawei/ad-name-template"
          element={
            <RequireAuth>
              <PlatformShell platform="huawei">
                <HuaweiAdNameTemplatePage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/huawei/redline"
          element={
            <RequireAuth>
              <PlatformShell platform="huawei">
                <HuaweiRedlineConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/huawei/strategy"
          element={
            <RequireAuth>
              <PlatformShell platform="huawei">
                <HuaweiStrategyConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/huawei/auto-operate-result"
          element={
            <RequireAuth>
              <PlatformShell platform="huawei">
                <HuaweiAutoAdOperateResultPage />
              </PlatformShell>
            </RequireAuth>
          }
        />

        {/* Apple Ads routes */}
        <Route
          path="/apple"
          element={
            <RequireAuth>
              <PlatformShell platform="apple">
                <Navigate to="/apple/ad-name-template" replace />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/apple/ad-name-template"
          element={
            <RequireAuth>
              <PlatformShell platform="apple">
                <AppleAdNameTemplatePage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/apple/redline"
          element={
            <RequireAuth>
              <PlatformShell platform="apple">
                <AppleRedlineConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/apple/strategy"
          element={
            <RequireAuth>
              <PlatformShell platform="apple">
                <AppleStrategyConfigPage />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/apple/auto-operate-result"
          element={
            <RequireAuth>
              <PlatformShell platform="apple">
                <AppleAutoAdOperateResultPage />
              </PlatformShell>
            </RequireAuth>
          }
        />

        {/* Honor（荣耀）routes — 四子页均为空页 */}
        <Route
          path="/honor"
          element={
            <RequireAuth>
              <PlatformShell platform="honor">
                <Navigate to="/honor/ad-name-template" replace />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/honor/ad-name-template"
          element={<EmptyRoute platform="honor" title="广告命名模板" />}
        />
        <Route
          path="/honor/redline"
          element={<EmptyRoute platform="honor" title="红线规则配置" />}
        />
        <Route
          path="/honor/strategy"
          element={<EmptyRoute platform="honor" title="策略配置" />}
        />
        <Route
          path="/honor/auto-operate-result"
          element={<EmptyRoute platform="honor" title="策略运行记录" />}
        />

        {/* VIVO routes — 四子页均为空页 */}
        <Route
          path="/vivo"
          element={
            <RequireAuth>
              <PlatformShell platform="vivo">
                <Navigate to="/vivo/ad-name-template" replace />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/vivo/ad-name-template"
          element={<EmptyRoute platform="vivo" title="广告命名模板" />}
        />
        <Route
          path="/vivo/redline"
          element={<EmptyRoute platform="vivo" title="红线规则配置" />}
        />
        <Route
          path="/vivo/strategy"
          element={<EmptyRoute platform="vivo" title="策略配置" />}
        />
        <Route
          path="/vivo/auto-operate-result"
          element={<EmptyRoute platform="vivo" title="策略运行记录" />}
        />

        {/* My resources（我的资源）routes — 跨平台共享资源入口 */}
        <Route
          path="/my-resources"
          element={
            <RequireAuth>
              <PlatformShell platform="my-resources">
                <Navigate to="/my-resources/business-city" replace />
              </PlatformShell>
            </RequireAuth>
          }
        />
        <Route
          path="/my-resources/business-city"
          element={
            <RequireAuth>
              <PlatformShell platform="my-resources">
                <BusinessCityPage />
              </PlatformShell>
            </RequireAuth>
          }
        />

        {/* 默认重定向 */}
        <Route path="/" element={<Navigate to="/tencent" replace />} />
        <Route path="*" element={<Navigate to="/tencent" replace />} />
      </Routes>
      <ToastViewport />
    </>
  );
}
